import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import {
  type LoaderFunction,
  useLoaderData,
  useNavigate,
  useRevalidator,
  useSearchParams,
} from "react-router-dom";

import { get, post, put } from "api";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import BillingInfo from "atoms/icon/icons/ic_billing_info_small_ol.svg";
import { Notification, useNotify } from "atoms/notification";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import {
  PAYMENT_UPDATED_PARAM,
  UPDATED_MEMBERSHIP_ID_PARAM,
} from "constants/index";
import { ManageMembershipCard } from "molecules/cards/manage-membership-card/ManageMembershipCard";
import { ActionModal } from "molecules/modal-window/action-modal/ActionModal";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";
import pluralize from "utils/pluralize";

import { handleUpdatePayment } from "./membershipHelper";
import { RetryMembershipPaymentFailedModal } from "./RetryMembershipPaymentFailedModal";

const load = async () => {
  const userMemberships = queryClient.fetchQuery(
    ["/platform-user-memberships"],
    () => get("/platform-user-memberships")
  );
  const userMembershipsStatus = queryClient.fetchQuery(
    ["/platform-user-memberships/status"],
    () => get("/platform-user-memberships/status")
  );

  const platform = queryClient.fetchQuery(["platform"], () => get("/platform"));
  const platformLocation = queryClient.fetchQuery(["/platform-location"], () =>
    get("/platform-location")
  );

  const data = await waitObj({
    platform,
    platformLocation,
    userMemberships,
    userMembershipsStatus,
  });

  if (data.userMemberships.length) {
    return { ...data };
  }

  return waitObj({ ...data });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipManageLoader: LoaderFunction = () => {
  return load();
};

export const WalletMembershipsManagePage = () => {
  const { platform, platformLocation, ...data } = useLoaderData() as LoaderData;

  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notificationProps, notify] = useNotify();

  /**
   * If `updated_membership` search param is present in the
   * URL and the updated membership it points to is in dunning,
   * it means that the payment method was successfully
   * updated which triggered automatic payment retry on the
   * backend. Knowing that, we switch on the polling mode &
   * wait for the membership to update.
   */
  const paymentMethodUpdateStatus = searchParams.get(PAYMENT_UPDATED_PARAM);
  const updatedMembershipId = searchParams.get(UPDATED_MEMBERSHIP_ID_PARAM);
  const initialUpdatedMembership = data.userMemberships.find(
    ({ platformUserMembershipId, dunningStage, membership }) =>
      platformUserMembershipId === updatedMembershipId &&
      dunningStage > 0 &&
      membership.active
  );
  const [isPolling, setIsPolling] = useState(
    paymentMethodUpdateStatus === "1" && !!initialUpdatedMembership
  );
  const [platformUserMembershipIdToRepay, setPlatformUserMembershipIdToRepay] =
    useState(initialUpdatedMembership?.platformUserMembershipId || "");
  const isPaymentMethodUpdateNotificationsSilencedRef = useRef(false);

  const [showModalId, setShowModalId] = useState<
    | "updatePaymentMethod"
    | "keepMembership"
    | "retryMembershipPaymentFailed"
    | undefined
  >(undefined);
  const [modalMembershipId, setModalMembershipId] = useState<
    string | undefined
  >(undefined);

  const { data: userMemberships } = useQuery({
    queryKey: ["/platform-user-memberships"],
    queryFn: () => get("/platform-user-memberships"),
    initialData: data.userMemberships,
    refetchInterval: (userMemberships) => {
      const userMembershipToRepay = userMemberships?.find(
        ({ platformUserMembershipId }) =>
          platformUserMembershipId === platformUserMembershipIdToRepay
      );
      const userMembershipOnDunning = userMemberships?.some(
        ({ canceled, dunningStage }) => !canceled && dunningStage
      );

      if (
        (!userMembershipToRepay && !userMembershipOnDunning) ||
        userMembershipToRepay?.dunningStage === 0
      ) {
        return false;
      }

      return userMembershipToRepay ? 1000 : 2000;
    },
  });

  // This API call is updated to know if the user is in dunning stage or has updated payment method
  const { data: statuses } = useQuery({
    queryKey: ["/platform-user-memberships/status"],
    queryFn: () => get("/platform-user-memberships/status"),
    initialData: data.userMembershipsStatus,
    // Membership cancellation happens async so we need to poll the data for status updates
    refetchInterval: (statuses) =>
      statuses?.some(({ canceled, dunningStage }) => !canceled && dunningStage)
        ? 2000
        : false,
  });

  const hideModal = () => {
    setModalMembershipId(undefined);
    setShowModalId(undefined);
  };

  const keepMembershipMutation = useMutation({
    mutationFn: (platformUserMembershipId: string) =>
      put(
        "/platform-user-memberships/{platformUserMembershipId}/keep-membership",
        {
          platformUserMembershipId,
        }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          [
            "/platform-user-memberships",
            "/platform-user-memberships/status",
          ].some((key) => query.queryKey[0] == key),
      });

      hideModal();
      notify.success("Membership changes saved.", {
        timeout: 5000,
        dismissable: false,
      });
    },
    onError: () => {
      notify.error(
        "Something went wrong - please contact support if this issue persists.",
        {
          timeout: 5000,
          dismissable: false,
        }
      );
    },
  });

  const addCanceledMembershipToCartMutation = useMutation({
    mutationFn: (membershipId: string) =>
      post("/orders/current/lineitems", {
        membershipId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/orders/current/summary"] });

      navigate("/cart");
    },
  });

  const retryMembershipPaymentMutation = useMutation({
    mutationFn: (platformUserMembershipId: string) =>
      put(
        "/platform-user-memberships/{platformUserMembershipId}/retry-membership-payment",
        {
          platformUserMembershipId,
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/platform-user-memberships"],
      });

      setIsPolling(true);
    },
    onError: () => {
      setPlatformUserMembershipIdToRepay("");
      setShowModalId("retryMembershipPaymentFailed");
    },
  });

  /**
   * Cancel polling mode after a fixed timeout.
   */
  useEffect(() => {
    if (!isPolling) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsPolling(false);
      setPlatformUserMembershipIdToRepay("");
      setSearchParams();
      isPaymentMethodUpdateNotificationsSilencedRef.current = true;
      notify.error(
        "Something went wrong - please contact support if this issue persists.",
        {
          timeout: 5000,
          dismissable: false,
        }
      );
    }, 60_000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isPolling, notify]);

  /**
   * Unset polling mode when polling is complete.
   */
  useEffect(() => {
    const userMembershipToRepay = userMemberships.find(
      ({ platformUserMembershipId }) =>
        platformUserMembershipId === platformUserMembershipIdToRepay
    );

    if (
      isPolling &&
      (!userMembershipToRepay || userMembershipToRepay.dunningStage === 0)
    ) {
      isPaymentMethodUpdateNotificationsSilencedRef.current = true;
      setIsPolling(false);
      setPlatformUserMembershipIdToRepay("");
      setSearchParams();
      notify.success("Payment successful!", {
        timeout: 5000,
        dismissable: false,
      });
    }
  }, [isPolling, notify, platformUserMembershipIdToRepay, userMemberships]);

  useEffect(() => {
    if (isPaymentMethodUpdateNotificationsSilencedRef.current) {
      return;
    }

    if (paymentMethodUpdateStatus === "1") {
      notify.success("Payment method updated successfully!", {
        timeout: 5000,
        dismissable: false,
      });
    } else if (paymentMethodUpdateStatus === "0") {
      notify.error("Payment method update unsuccessful, please try again", {
        timeout: 5000,
        dismissable: false,
      });
    }
  }, [notify, paymentMethodUpdateStatus]);

  const modalMembership = userMemberships?.find(
    ({ platformUserMembershipId }) =>
      platformUserMembershipId === modalMembershipId
  );

  const activeMemberships = userMemberships.filter(
    ({ canceled }) => !canceled
  ).length;

  if (isPolling) {
    return (
      <div className="flex flex-col items-center gap-8 px-8 pb-8 pt-[calc(20px_+_env(safe-area-inset-top))]">
        <TailSpin
          height="80"
          width="80"
          radius="1"
          color="rgb(var(--repeatmd-brand-color))"
        />
        <div className="pb-12 text-center font-serif text-h2">
          <div>Just a moment...</div>
          <div>Retrying your payment.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col bg-one">
      <Notification {...notificationProps} />
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">MANAGE MEMBERSHIPS</div>
      </TopNav>
      <div className="flex flex-col gap-y-4 pt-4 text-center">
        <div className="text-center text-body2 text-secondary">
          {activeMemberships} active{" "}
          {pluralize(activeMemberships, "membership", "memberships")}
        </div>
        <div className="flex flex-col gap-y-4 px-4">
          {userMemberships.map((item) => {
            const membershipStatus = statuses.find(({ id }) => id == item.id);

            if (!membershipStatus) {
              return null;
            }

            return (
              <ManageMembershipCard
                key={item.id}
                membership={item}
                disableCancellationAllowed={
                  platformLocation.supportsMinimumCommitmentPeriod
                }
                paymentMethodLast4={item.paymentMethodDetails?.last4}
                onReactivateMembership={() => {
                  addCanceledMembershipToCartMutation.mutate(
                    item.membership.id
                  );
                }}
                onUpdatePaymentMethod={() => {
                  setModalMembershipId(item.platformUserMembershipId);
                  setShowModalId("updatePaymentMethod");
                }}
                onKeepMembership={() => {
                  setModalMembershipId(item.platformUserMembershipId);
                  setShowModalId("keepMembership");
                }}
                membershipStatus={membershipStatus}
                onRetryMembershipPayment={() => {
                  if (!item.platformUserMembershipId) {
                    return;
                  }

                  setPlatformUserMembershipIdToRepay(
                    item.platformUserMembershipId
                  );
                  setModalMembershipId(item.platformUserMembershipId);
                  retryMembershipPaymentMutation.mutate(
                    item.platformUserMembershipId
                  );
                }}
              />
            );
          })}
        </div>
      </div>
      <div className="grow" />
      <div className="flex w-full scroll-py-4 flex-col items-center">
        <RepeatMDFooter />
      </div>
      {showModalId === "updatePaymentMethod" && modalMembershipId && (
        <ActionModal
          size="small"
          open
          title="Update payment method"
          description="Continuing will take you to a new page to update your membership payment method. Once updated, your new payment method will be used for this membership moving forward."
          firstButtonLabel="Update payment method"
          onFirstButtonClick={() =>
            handleUpdatePayment(modalMembershipId, platform, () => {
              const isRetryingMembershipPayment =
                !!modalMembership?.dunningStage &&
                modalMembership.membership.active;
              hideModal();

              if (isRetryingMembershipPayment) {
                setSearchParams({
                  [PAYMENT_UPDATED_PARAM]: "1",
                  [UPDATED_MEMBERSHIP_ID_PARAM]:
                    modalMembership.platformUserMembershipId,
                });
                return;
              }

              revalidate();
              notify.success("Payment method updated successfully!", {
                timeout: 5000,
                dismissable: false,
              });
            })
          }
          headerIconSvg={BillingInfo}
          secondButtonLabel="Cancel"
          onSecondButtonClick={hideModal}
          onClose={hideModal}
        />
      )}
      {showModalId === "keepMembership" && modalMembershipId && (
        <ActionModal
          size="small"
          open
          title="Are you sure you want to keep your membership?"
          description={`Your ${
            modalMembership?.membership?.name
          } membership will remain active and renew on ${dayjs(
            modalMembership?.expiration
          ).format("MMM D, YYYY")}`}
          firstButtonLabel="Confirm"
          onFirstButtonClick={() =>
            keepMembershipMutation.mutate(modalMembershipId)
          }
          secondButtonLabel="Cancel"
          onSecondButtonClick={hideModal}
          onClose={hideModal}
        />
      )}
      {showModalId === "retryMembershipPaymentFailed" && modalMembershipId && (
        <RetryMembershipPaymentFailedModal
          paymentMethodLast4={modalMembership?.paymentMethodDetails?.last4}
          onClose={hideModal}
        />
      )}
    </div>
  );
};
